/**
 * Shared Hour class used by DayHours and HolidayHours classes
 *
 * @author MattJS (matt@yext.com)
 */

goog.module('yext.location.hours.Hour');
goog.module.declareLegacyNamespace();

/**
 * Hour model
 *
 * Represents hours for a particular day or date
 *
 * TODO(mattjs) : Consider extending this for clarity with holiday hours
 *
 * @param {!Object} data Object for setting initial values
 * @constructor
 */
const Hour = function(data) {
  data = typeof data === 'object' ? data : {};
  data.intervals = Object.prototype.toString.call(data.intervals) === '[object Array]' ? data.intervals : [];
  $.extend(this, data);
};

/**
 * This converts an array of intervals to an array of updates to intervals.
 * The updates specify which interval was updated using the index, property
 * (start/end) and value for setting an hour in a particular interval
 *
 * @param {!Array} intervals Array of hour intervals with a start and end
 * @return {!Array} A interval array converted to an update array (index, property and value)
 */
Hour.intervalsToUpdate = function(intervals) {
  var update = [];
  intervals = intervals || [];
  for (var i = 0; i < 2; i++) {
    var start = '';
    var end = '';

    if (intervals[i]) {
      start = intervals[i].start;
      end = intervals[i].end;
    }
    update.push({
      index: i,
      property: 'start',
      value: start,
    });
    update.push({
      index: i,
      property: 'end',
      value: end,
    });
  }
  return update;
};

/**
 * Have to clone intervals manually because slice does not do deep copy
 *
 * @param {!Array} interval Interval array
 * @return {!Array} A copy of the interval
 */
Hour.cloneIntervals = function(interval) {
  var clone = [];
  for (var i = 0; i < interval.length; i++) {
    clone.push({
      start: interval[i].start,
      end: interval[i].end,
    });
  }
  return clone;
};

/**
 * @return {!Array} intervals for always open hour type
 */
Hour.prototype.getAlwaysOpenIntervalsUpdate = function() {
  var intervals = [];
  intervals.push({index: 0, property: 'start', value: '12:00 AM'});
  intervals.push({index: 1, property: 'end', value: '12:00 AM'});
  return intervals;
};

/**
 * Update the hours model
 *
 * @param {!Object} data Updates object to update the Hour model
 */
Hour.prototype.update = function(data) {
  // Type safety
  if (typeof data !== 'object') {
    return;
  }

  var intervalChanges;
  // If type is changed to closed or regular hours, clear out interval
  if (data.type) {
    if (data.type == 'CLOSED' || data.type == 'IS_REGULAR_HOURS') {
      delete data.intervals;
      this.intervals = [];
      intervalChanges = Hour.intervalsToUpdate([]);
    } else if (data.type == 'OPEN') {
      if (this.intervals[0]) {
        this.intervals = [this.intervals[0]];
      }
      intervalChanges = Hour.intervalsToUpdate(this.intervals);
    } else if (data.type == 'ALWAYS_OPEN') {
      intervalChanges = this.getAlwaysOpenIntervalsUpdate();
    }
  }

  if (data.intervals) {
    intervalChanges = this.setIntervals(data.intervals);
    delete data.intervals;
  }

  var changes = this.getChanges(data);

  if (changes || intervalChanges) {
    if (changes) {
      $.extend(this, changes);
    } else {
      changes = {};
    }
    if (intervalChanges) {
      changes.intervals = intervalChanges;
    }
    this.broadcast(changes);
  }
};

/**
 * Update a particular hour on an interval
 *
 * @param {number} index The index of the interval
 * @param {string} property Either start or end
 * @param {string} value The value that is being set for this interval hour
 * @param {string} format
 */
Hour.prototype.updateInterval = function(index, property, value, format) {
  this.update({
    intervals: [{
      index: index,
      property: property,
      value: value,
      format: format,
    }],
  });
};

/**
 * Filter a update object to include only changed properties
 *
 * @param {!Object} data Update object
 * @return {!Object} Object of changes to the model
 */
Hour.prototype.getChanges = function(data) {
  var changed = false;
  var changes = {};
  for (var prop in data) {
    if (data[prop] != this[prop]) {
      changed = true;
      changes[prop] = data[prop];
    }
  }
  return changed ? changes : false;
};

/**
 * Sets the value of the hours model intervals property with a intervals update
 * object
 *
 * @param {!Array} intervals Intervals update array
 * @return {!Array|boolean}
 */
Hour.prototype.setIntervals = function(intervals) {
  var self = this;
  var changes = [];

  intervals.forEach(function(interval) {
    if (!self.intervals[interval.index]) {
      self.intervals[interval.index] = {};
    }

    var value = self.formatHours(interval);

    if (self.intervals[interval.index][interval.property] != value) {
      self.intervals[interval.index][interval.property] = value;
      changes.push({
        index: interval.index,
        property: interval.property,
        value: value,
      });
    }
  });

  return changes.length ? changes : false;
};

/**
 * Format a time using and interval update
 *
 * @param {!Object} interval Interval update
 * @return {string} Formatted hours value
 */
Hour.prototype.formatHours = function(interval) {
  if (yext.localization.isUSLocale()
    && interval.format
    && interval.value
    && !yext.time.containsMeridium(interval.value)) {
    var value = interval.value;
    var hours = parseInt(value, 10);
    var isStart = interval.property == 'start';
    var relatedValue = this.intervals[interval.index][isStart ? 'end' : 'start'];
    var meridiem = interval.index == 1 ? 'PM' : (isStart && hours != 12 ? 'AM' : 'PM');
    if (hours <= 12
        && (!relatedValue || parseInt(relatedValue, 10) <= 12)) {
      value += ' ' + meridiem;
    }
  } else {
    value = interval.value;
  }
  return value;
};

/**
 * Copy the properties of another Hour model
 *
 * @param {Hour} hour Another hour model from which to copy properties
 */
Hour.prototype.copy = function(hour) {
  this.update({
    date: hour.date,
    type: hour.type,
    intervals: Hour.intervalsToUpdate(hour.intervals),
    dateIndeterminate: hour.dateIndeterminate,
    intervalIndeterminate: hour.intervalIndeterminate,
    selected: hour.selected,
  });
};

/**
 * Make a clone of the hour for saving its state
 *
 * @return {Hour} Copy of Hour model
 */
Hour.prototype.clone = function() {
  return new Hour({
    day: this.day,
    date: this.date,
    type: this.type,
    intervals: Hour.cloneIntervals(this.intervals),
    dateIndeterminate: this.dateIndeterminate,
    intervalIndeterminate: this.intervalIndeterminate,
    selected: this.selected,
  });
};

/**
 * Reset the hour to an empty state
 */
Hour.prototype.clear = function() {
  this.update({
    type: 'OPEN',
    intervals: [{
      index: 0,
      property: 'start',
      value: '',
    }, {
      index: 0,
      property: 'end',
      value: '',
    }],
  });
};

/**
 * Broadcast that an update has been made to the hour
 *
 * @param {!Object} update Details about which properties have been updated
 */
Hour.prototype.broadcast = function(update) {
  $(this).trigger('updated.hour', update);
};

exports = Hour;
